/**
 * 10.9AG — THE RIFT SEER (Fase 4): boss CONJURADOR que luta À DISTÂNCIA. Diferente do Apex Fang
 * (melee) e do Colossus (tanque com placas): o Seer FLUTUA, kita mantendo distância e telegrafa
 * TUDO — barragem no chão (posição prevista), feixe em linha, blink+nova (janela de HOOK pra
 * interromper) e anel expansivo. Morte por ERRO DE LEITURA, nunca "sem como saber". Câmera só com
 * impulso sutil. A lógica fica no chão (y=0, alvo/colisão iguais aos outros bosses); só o VISUAL flutua.
 */
import * as THREE from "three";
import { GameConfig } from "../config/GameConfig";
import { applyRim } from "../fx/RimLight";
import type { Damageable } from "../combat/Damageable";
import type { HookReaction } from "../player/HookSystem";
import type { TelegraphSystem } from "../fx/Telegraph";
import type { ParticleSystem } from "../fx/Particles";
import type { Boss, BossDebug } from "./Boss";
import { HealthComponent } from "../combat/HealthComponent";
import { dealDamage } from "../combat/DamageSystem";
import { events } from "../core/Events";
import { dampAngle } from "../utils/math";
import type { SeerForm } from "./BossForms";
import { furyMult } from "./Fury"; // 10.9BF

export type SeerState = "intro" | "idle" | "attack" | "stagger" | "phaseTransition" | "dead";
export type SeerAttack = "barrage" | "beam" | "blinkNova" | "ring";

export interface RiftSeerDeps {
  player: Damageable;
  telegraph: TelegraphSystem;
  particles: ParticleSystem;
  impulse: (strength: number) => void;
}

export interface RiftSeerOptions {
  event?: boolean;
  hpMult?: number;
  dmgMult?: number;
  spawnAt?: { x: number; z: number };
  tint?: number;
  titleKey?: string;
  arenaRadius?: number; // 10.9AJ — raio da arena desta instância (salas da Zona dos Bosses)
  form?: SeerForm; // 10.9AT — morfologia da variante (anéis, cristais, tentáculos, olho, halo)
}

const cfg = GameConfig.boss.riftSeer;
const ACCENT = 0x8f6bff; // arcano
const DANGER = 0xff4d7a;

const _v = new THREE.Vector3();
const _tmp = new THREE.Vector3();

export class RiftSeer implements Boss {
  readonly group = new THREE.Group();
  readonly health: HealthComponent;
  readonly radius = cfg.radius;
  pulled = false;

  state: SeerState = "intro";
  removeRequested = false;

  private stateTimer: number = cfg.introDuration;
  private attackName: SeerAttack | null = null;
  private atkPhase = 0;
  private atkTimer = 0;
  private phase = 1 as number;
  private restTimer = 0;
  private facing = Math.PI;
  private flash = 0;
  private deathTimer = 0;
  private atkTeleId = 0;
  private spin = 0;
  private hookWindow = 0; // >0 durante o channel do blinkNova (o Hook interrompe)

  private readonly cd: Record<SeerAttack, number> = { barrage: 0, beam: 0, blinkNova: 0, ring: 0 };

  // antecipação de movimento (barrage/blink miram onde o player VAI estar)
  private readonly lastPlayerPos = new THREE.Vector3();
  private readonly playerVel = new THREE.Vector3();
  private velInit = false;

  // barragem / anel pendentes
  private readonly pending: { x: number; z: number; r: number; t: number; dmg: number }[] = [];
  private ringRadius = 0;

  private readonly arenaCx: number;
  private readonly arenaCz: number;
  private readonly isEvent: boolean;
  private readonly baseDmgMult: number;
  private get dmgMult(): number { return this.baseDmgMult * furyMult(this.health.fraction); } // 10.9BF
  private readonly titleKey?: string;
  private readonly arenaR: number; // 10.9AJ

  private readonly coreMat: THREE.MeshStandardMaterial;
  private readonly ringMats: THREE.MeshBasicMaterial[] = [];
  private readonly auraMat: THREE.MeshBasicMaterial;
  private readonly floatGroup = new THREE.Group(); // VISUAL flutuante (a lógica fica no chão)
  private readonly shards: THREE.Mesh[] = [];
  private auraPulse = 0;

  constructor(scene: THREE.Scene, private readonly deps: RiftSeerDeps, opts?: RiftSeerOptions) {
    this.isEvent = opts?.event === true;
    this.baseDmgMult = opts?.dmgMult ?? 1;
    this.arenaCx = opts?.spawnAt?.x ?? 0;
    this.arenaCz = opts?.spawnAt?.z ?? 0;
    this.titleKey = opts?.titleKey;
    this.arenaR = opts?.arenaRadius ?? cfg.arena.radius;
    this.health = new HealthComponent(Math.round(cfg.maxHp * (opts?.hpMult ?? 1)), undefined, () => this.die());

    const tint = opts?.tint ?? ACCENT;
    // 10.9BT: núcleo 1.4 → 1.2 em repouso (bloom threshold 0.85) + luz de borda na cor da variante
    this.coreMat = new THREE.MeshStandardMaterial({ color: 0x140f2e, emissive: tint, emissiveIntensity: 1.2, roughness: 0.3, metalness: 0.3, flatShading: true });
    applyRim(this.coreMat, { color: tint, power: 2.4, strength: 1.0 });
    this.auraMat = new THREE.MeshBasicMaterial({ color: tint, transparent: true, opacity: 0.4, depthWrite: false, side: THREE.DoubleSide });
    this.buildMesh(tint, opts?.form ?? {});

    this.group.add(this.floatGroup);
    const aura = new THREE.Mesh(new THREE.RingGeometry(this.radius * 1.5, this.radius * 2.1, 40), this.auraMat);
    aura.rotation.x = -Math.PI / 2;
    aura.position.y = 0.05;
    this.group.add(aura);

    this.group.position.set(this.arenaCx, 0, this.arenaCz);
    if (this.isEvent) this.group.scale.setScalar(1.12);
    scene.add(this.group);
    events.emit("boss:spawned", undefined);
  }

  private buildMesh(tint: number, form: SeerForm): void {
    const r = this.radius;
    const eye = form.eyeScale ?? 1;
    // núcleo (olho arcano) flutuando
    const core = new THREE.Mesh(new THREE.IcosahedronGeometry(r * 0.72, 0), this.coreMat);
    core.castShadow = true;
    this.floatGroup.add(core);
    const pupil = new THREE.Mesh(new THREE.SphereGeometry(r * 0.3 * eye, 10, 8), new THREE.MeshBasicMaterial({ color: 0xffffff }));
    pupil.position.z = r * 0.55;
    pupil.scale.set(1, 1, 0.5);
    this.floatGroup.add(pupil);
    const iris = new THREE.Mesh(new THREE.SphereGeometry(r * 0.16 * eye, 8, 8), new THREE.MeshBasicMaterial({ color: DANGER }));
    iris.position.z = r * 0.72;
    this.floatGroup.add(iris);
    // 10.9AT — halo grande atrás do núcleo
    if (form.halo) {
      const halo = new THREE.Mesh(new THREE.RingGeometry(r * 1.5, r * 2.2, 32), new THREE.MeshBasicMaterial({ color: tint, transparent: true, opacity: 0.35, side: THREE.DoubleSide, depthWrite: false, blending: THREE.AdditiveBlending }));
      halo.position.z = -r * 0.4;
      this.floatGroup.add(halo);
    }
    // 10.9AT — tentáculos pendurados sob o núcleo
    const nT = form.tentacles ?? 0;
    for (let i = 0; i < nT; i++) {
      const a = (i / nT) * Math.PI * 2;
      const t = new THREE.Mesh(new THREE.ConeGeometry(r * 0.12, r * 1.6, 5), this.coreMat);
      t.position.set(Math.cos(a) * r * 0.45, -r * 1.1, Math.sin(a) * r * 0.45);
      t.rotation.x = Math.PI + Math.sin(a) * 0.3;
      t.rotation.z = Math.cos(a) * 0.3;
      this.floatGroup.add(t);
    }
    // anéis orbitais (base 2)
    for (let i = 0; i < (form.rings ?? 2); i++) {
      const m = new THREE.MeshBasicMaterial({ color: tint, transparent: true, opacity: 0.75, side: THREE.DoubleSide });
      this.ringMats.push(m);
      const ring = new THREE.Mesh(new THREE.TorusGeometry(r * (1.2 + i * 0.35), r * 0.06, 6, 28), m);
      ring.rotation.x = i === 0 ? Math.PI / 2.4 : Math.PI / 3.5;
      ring.rotation.y = i * 0.6;
      this.floatGroup.add(ring);
    }
    // cristais orbitando
    const shardMat = new THREE.MeshStandardMaterial({ color: tint, emissive: tint, emissiveIntensity: 0.9, roughness: 0.3, flatShading: true });
    applyRim(shardMat, { color: tint, power: 2.4, strength: 0.9 }); // 10.9BT
    const nS = form.shards ?? 5;
    for (let i = 0; i < nS; i++) {
      const s = new THREE.Mesh(new THREE.OctahedronGeometry(r * 0.2, 0), shardMat);
      const a = (i / nS) * Math.PI * 2;
      s.position.set(Math.cos(a) * r * 1.7, Math.sin(a) * r * 0.5, Math.sin(a) * r * 1.7);
      this.shards.push(s);
      this.floatGroup.add(s);
    }
    this.floatGroup.position.y = cfg.hover;
  }

  // ---- Damageable ----
  get position(): THREE.Vector3 { return this.group.position; }
  get isDead(): boolean { return this.state === "dead"; }
  get invulnerable(): boolean { return this.state === "intro" || this.state === "phaseTransition" || this.state === "dead"; }
  get armor(): number { return cfg.armor; }
  onHit(): void { if (!this.isDead) this.flash = 0.1; }
  applyKnockback(): void { /* flutua: sem knockback */ }

  // ---- HookTarget ----
  onHooked(): HookReaction {
    // só interrompe durante a JANELA do channel do blinkNova (recompensa de leitura)
    if (this.attackName === "blinkNova" && this.hookWindow > 0) {
      this.interruptChannel();
      return "grab";
    }
    return "none"; // fora disso: ricochete (não puxa o boss)
  }

  // ---- Boss ----
  get hpFraction(): number { return Math.max(0, this.health.fraction); }
  get phaseNumber(): number { return this.phase; }
  get nameKey(): string { return this.titleKey ?? "boss.riftSeer"; }
  /** 10.9AK — arena desta instância (o Game mantém o player dentro: sem matar de fora da linha). */
  get arenaInfo(): { x: number; z: number; r: number } { return { x: this.arenaCx, z: this.arenaCz, r: this.arenaR }; }
  private get isRage(): boolean { return this.phase >= 2; }
  private get atkScale(): number { return this.isRage ? cfg.phase2AttackScale : 1; }
  debugSnapshot(): BossDebug {
    return { state: this.state, attack: this.attackName ?? "-", phase: this.phase, hp: Math.ceil(this.health.current) };
  }

  // ---- loop ----
  update(dt: number): void {
    if (this.state === "dead") { this.updateDeath(dt); return; }
    (Object.keys(this.cd) as SeerAttack[]).forEach((k) => (this.cd[k] = Math.max(0, this.cd[k] - dt)));
    this.restTimer = Math.max(0, this.restTimer - dt);
    this.hookWindow = Math.max(0, this.hookWindow - dt);
    this.trackPlayerVel(dt);
    this.updatePending(dt);

    if (this.flash > 0) { this.flash -= dt; this.coreMat.emissiveIntensity = this.flash > 0 ? 2.6 : this.isRage ? 1.2 : 1.0; } // 10.9BT: repouso ≤1.2
    this.auraPulse += dt * (this.isRage ? 6 : 3);
    this.auraMat.opacity = 0.3 + Math.sin(this.auraPulse) * 0.14 + (this.isRage ? 0.14 : 0);
    // vida visual: flutua, anéis giram, cristais orbitam
    this.spin += dt;
    this.floatGroup.position.y = cfg.hover + Math.sin(this.auraPulse * 0.8) * 0.18;
    this.floatGroup.rotation.y = this.spin * 0.6;
    // 10.9AV — ANTECIPAÇÃO: no windup o núcleo se inclina pro player e incha (carregando); ao
    // disparar, recua um pouco (recoil). Lê-se o ataque antes de ele sair.
    const windup = this.state === "attack" && this.atkPhase === 0 ? 1 : 0;
    const recoil = this.state === "attack" && this.atkPhase >= 1 ? 1 : 0;
    const tiltT = windup * 0.35 - recoil * 0.2;
    this.floatGroup.rotation.x += (tiltT - this.floatGroup.rotation.x) * Math.min(1, dt * 8);
    const sc = 1 + windup * (0.08 + Math.sin(this.auraPulse * 4) * 0.04) - recoil * 0.05;
    this.floatGroup.scale.setScalar(sc);
    if (this.ringMats.length) { /* opacidade pulsa com a fúria */ this.ringMats[0].opacity = 0.6 + Math.sin(this.auraPulse * 1.5) * 0.25; }
    for (let i = 0; i < this.shards.length; i++) {
      const a = (i / this.shards.length) * Math.PI * 2 + this.spin * 1.2;
      this.shards[i].position.set(Math.cos(a) * this.radius * 1.7, Math.sin(a * 1.3) * this.radius * 0.5, Math.sin(a) * this.radius * 1.7);
      this.shards[i].rotation.y += dt * 2;
    }

    this.checkPhase();
    switch (this.state) {
      case "intro": this.updateIntro(dt); break;
      case "phaseTransition": this.updateTransition(dt); break;
      case "stagger": this.updateStagger(dt); break;
      case "idle": this.updateIdle(dt); break;
      case "attack": this.updateAttack(dt); break;
    }

    this.group.position.y = 0; // lógica no chão
    this.updateFacing(dt);
    this.group.rotation.y = this.facing;
  }

  private updateIntro(dt: number): void {
    this.stateTimer -= dt;
    if (Math.random() < dt * 3) this.deps.particles.burst(this.group.position.x, cfg.hover, this.group.position.z, ACCENT, { count: 8, speed: 3 });
    if (this.stateTimer <= 0) this.state = "idle";
  }

  private checkPhase(): void {
    if (this.state === "intro" || this.state === "phaseTransition" || this.state === "dead") return;
    if (this.phase < 2 && this.health.fraction <= cfg.phase2At) this.enterPhase2();
  }
  private enterPhase2(): void {
    this.phase = 2;
    this.state = "phaseTransition";
    this.stateTimer = 1.1;
    this.deps.telegraph.cancel(this.atkTeleId); this.atkTeleId = 0;
    this.attackName = null; this.atkPhase = 0; this.hookWindow = 0;
    (Object.keys(this.cd) as SeerAttack[]).forEach((k) => (this.cd[k] = 0));
    this.deps.impulse(0.16);
    this.deps.particles.burst(this.group.position.x, cfg.hover, this.group.position.z, DANGER, { count: 26, speed: 8, dur: 0.8 });
    events.emit("boss:phase", { phase: 2 });
  }
  private updateTransition(dt: number): void {
    this.stateTimer -= dt;
    this.coreMat.emissiveIntensity = 1.6 + Math.sin(this.stateTimer * 20) * 0.7;
    if (this.stateTimer <= 0) { this.coreMat.emissiveIntensity = this.isRage ? 1.2 : 1.0; this.state = "idle"; } // 10.9BT
  }
  private updateStagger(dt: number): void {
    this.stateTimer -= dt;
    if (this.stateTimer <= 0) { this.state = "idle"; this.restTimer = 0.4; }
  }

  // ---- idle: KITA mantendo distância + escolhe ataque ----
  private updateIdle(dt: number): void {
    const dist = this.distToPlayer();
    // reposiciona: afasta se perto, aproxima só se longe demais (mantém keepDist)
    _v.subVectors(this.deps.player.position, this.group.position); _v.y = 0;
    const d = _v.length() || 1;
    const speed = cfg.moveSpeed * (this.isRage ? 1.2 : 1);
    if (dist < cfg.keepDist - 1) this.group.position.addScaledVector(_v, -(speed * dt) / d); // recua
    else if (dist > cfg.keepDist + 4) this.group.position.addScaledVector(_v, (speed * dt) / d); // reaproxima
    this.clampToArena(this.group.position);

    if (this.restTimer > 0) return;
    const atk = this.selectAttack(dist);
    if (atk) this.beginAttack(atk);
  }

  private selectAttack(dist: number): SeerAttack | null {
    const opts: SeerAttack[] = [];
    if (this.cd.barrage <= 0) opts.push("barrage");
    if (this.cd.beam <= 0) opts.push("beam");
    if (this.cd.ring <= 0 && dist < cfg.attacks.ring.maxRadius) opts.push("ring");
    if (this.cd.blinkNova <= 0) opts.push("blinkNova");
    if (opts.length === 0) return null;
    return opts[Math.floor(Math.random() * opts.length)];
  }

  private beginAttack(name: SeerAttack): void {
    this.attackName = name;
    this.state = "attack";
    this.atkPhase = 0;
    const s = this.atkScale;
    const a = cfg.attacks;
    switch (name) {
      case "barrage": {
        this.atkTimer = a.barrage.windup * s;
        const life = this.atkTimer + a.barrage.delay;
        for (let i = 0; i < a.barrage.count; i++) {
          this.predictPlayer(life * (0.5 + i * 0.12), _tmp); // escalona a mira ao longo do tempo
          const ang = Math.random() * Math.PI * 2;
          const rr = i === 0 ? 0 : Math.random() * a.barrage.spread;
          const zx = _tmp.x + Math.cos(ang) * rr;
          const zz = _tmp.z + Math.sin(ang) * rr;
          this.deps.telegraph.circle(zx, zz, a.barrage.radius, life, DANGER);
          this.pending.push({ x: zx, z: zz, r: a.barrage.radius, t: life, dmg: a.barrage.damage });
        }
        break;
      }
      case "beam": {
        this.atkTimer = a.beam.windup * s;
        this.faceInstant(this.deps.player.position);
        const ang = this.facing;
        this.atkTeleId = this.deps.telegraph.lane(this.group.position.x, this.group.position.z, ang, a.beam.length, a.beam.width, this.atkTimer, DANGER);
        break;
      }
      case "ring": {
        this.atkTimer = a.ring.windup * s;
        this.ringRadius = 0;
        this.atkTeleId = this.deps.telegraph.circle(this.group.position.x, this.group.position.z, a.ring.maxRadius, this.atkTimer, ACCENT);
        break;
      }
      case "blinkNova": {
        // teleporta pra perto do player (num flanco), depois canaliza (janela de hook) e estoura
        this.predictPlayer(0.1, _tmp);
        const ang = Math.random() * Math.PI * 2;
        this.group.position.set(_tmp.x + Math.cos(ang) * 5, 0, _tmp.z + Math.sin(ang) * 5);
        this.clampToArena(this.group.position);
        this.deps.particles.burst(this.group.position.x, cfg.hover, this.group.position.z, ACCENT, { count: 18, speed: 7, dur: 0.6 });
        this.atkTimer = a.blinkNova.channel * s;
        this.hookWindow = 0; // ativado na fase de channel
        this.atkTeleId = this.deps.telegraph.circle(this.group.position.x, this.group.position.z, a.blinkNova.radius, a.blinkNova.windup * s + this.atkTimer, DANGER);
        break;
      }
    }
  }

  private updateAttack(dt: number): void {
    switch (this.attackName) {
      case "barrage": this.runBarrage(dt); break;
      case "beam": this.runBeam(dt); break;
      case "ring": this.runRing(dt); break;
      case "blinkNova": this.runBlinkNova(dt); break;
    }
  }

  private finishAttack(name: SeerAttack): void {
    this.cd[name] = cfg.attacks[name].cooldown;
    this.attackName = null;
    this.atkPhase = 0;
    this.state = "idle";
    this.restTimer = (this.isRage ? cfg.restRage : cfg.restBetweenAttacks);
  }

  private runBarrage(dt: number): void {
    // só espera o windup; o dano das erupções resolve em updatePending
    this.atkTimer -= dt;
    if (this.atkTimer <= 0) this.finishAttack("barrage");
  }

  private runBeam(dt: number): void {
    const a = cfg.attacks.beam;
    if (this.atkPhase === 0) {
      this.atkTimer -= dt;
      if (this.atkTimer <= 0) {
        this.hitLane(this.facing, a.length, a.width, a.damage);
        this.deps.impulse(0.14);
        this.deps.particles.burst(this.group.position.x + Math.sin(this.facing) * 3, 0.4, this.group.position.z + Math.cos(this.facing) * 3, DANGER, { count: 16, speed: 9, dur: 0.5 });
        this.atkTeleId = 0;
        this.atkPhase = 1;
        this.atkTimer = a.recovery;
      }
    } else if (this.atkTimer <= 0) this.finishAttack("beam");
    else this.atkTimer -= dt;
  }

  private runRing(dt: number): void {
    const a = cfg.attacks.ring;
    if (this.atkPhase === 0) {
      this.atkTimer -= dt;
      if (this.atkTimer <= 0) {
        this.atkPhase = 1;
        this.ringRadius = 0;
        this.deps.impulse(0.16);
        this.deps.telegraph.wave(this.group.position.x, this.group.position.z, a.maxRadius, a.maxRadius / a.waveSpeed, ACCENT);
      }
    } else if (this.atkPhase === 1) {
      this.ringRadius += a.waveSpeed * dt;
      const d = this.distToPlayer();
      if (!this.deps.player.invulnerable && this.ringRadius > 2 && Math.abs(d - this.ringRadius) < 1.4) {
        this.hitPlayer(a.damage, 8, this.group.position);
      }
      if (this.ringRadius >= a.maxRadius) { this.atkPhase = 2; this.atkTimer = a.recovery; }
    } else if (this.atkTimer <= 0) this.finishAttack("ring");
    else this.atkTimer -= dt;
  }

  private runBlinkNova(dt: number): void {
    const a = cfg.attacks.blinkNova;
    const s = this.atkScale;
    if (this.atkPhase === 0) {
      // channel: janela de HOOK ativa entre hookStart..hookEnd (fração do channel)
      this.atkTimer -= dt;
      const elapsed = a.channel * s - this.atkTimer;
      this.hookWindow = elapsed >= a.hookStart * s && elapsed <= a.hookEnd * s ? 1 : 0;
      if (this.atkTimer <= 0) {
        this.hookWindow = 0;
        this.hitPlayer(a.damage, 6, this.group.position, a.radius); // estoura o AoE
        this.deps.impulse(0.2);
        this.deps.particles.burst(this.group.position.x, 0.5, this.group.position.z, DANGER, { count: 26, speed: 9, dur: 0.7 });
        this.atkPhase = 1;
        this.atkTimer = a.recovery;
      }
    } else if (this.atkTimer <= 0) this.finishAttack("blinkNova");
    else this.atkTimer -= dt;
  }

  private interruptChannel(): void {
    this.deps.telegraph.cancel(this.atkTeleId); this.atkTeleId = 0;
    this.attackName = null; this.atkPhase = 0; this.hookWindow = 0;
    this.cd.blinkNova = cfg.attacks.blinkNova.cooldown;
    this.state = "stagger";
    this.stateTimer = cfg.attacks.blinkNova.interruptStagger;
    this.deps.particles.burst(this.group.position.x, cfg.hover, this.group.position.z, 0x9d7bff, { count: 20, speed: 6 });
    events.emit("boss:interrupted", undefined);
  }

  // ---- resolução das erupções (barragem) ----
  private updatePending(dt: number): void {
    if (this.pending.length === 0) return;
    const p = this.deps.player;
    for (let i = this.pending.length - 1; i >= 0; i--) {
      const z = this.pending[i];
      z.t -= dt;
      if (z.t > 0) continue;
      this.pending.splice(i, 1);
      this.deps.particles.burst(z.x, 0.3, z.z, DANGER, { count: 10, speed: 7, dur: 0.5 });
      const dx = p.position.x - z.x, dz = p.position.z - z.z;
      if (!p.invulnerable && dx * dx + dz * dz <= z.r * z.r) {
        dealDamage(p, { baseDamage: z.dmg * this.dmgMult + this.chip(), critChance: 0, critDamageMult: 1, armor: 0 }, { targetKind: "player", attackerRank: "boss", damageType: "piercing", knockbackFrom: new THREE.Vector3(z.x, 0, z.z), knockbackForce: 5 });
      }
    }
  }

  // ---- helpers de dano ----
  private chip(): number { return this.deps.player.health.max * cfg.hpChipPct; }

  private hitPlayer(damage: number, knockback: number, from: THREE.Vector3, radius?: number): void {
    const p = this.deps.player;
    if (p.invulnerable) return;
    if (radius !== undefined) {
      const dx = p.position.x - from.x, dz = p.position.z - from.z;
      if (dx * dx + dz * dz > radius * radius) return;
    }
    dealDamage(p, { baseDamage: damage * this.dmgMult + this.chip(), critChance: 0, critDamageMult: 1, armor: 0 }, { targetKind: "player", attackerRank: "boss", damageType: "blunt", knockbackFrom: from, knockbackForce: knockback });
  }

  private hitLane(dir: number, length: number, width: number, damage: number): void {
    const p = this.deps.player;
    if (p.invulnerable) return;
    _v.subVectors(p.position, this.group.position); _v.y = 0;
    const dx = Math.sin(dir), dz = Math.cos(dir);
    const along = _v.x * dx + _v.z * dz;
    if (along < 0 || along > length) return;
    const perp = Math.abs(_v.x * dz - _v.z * dx);
    if (perp > width / 2 + 0.5) return;
    dealDamage(p, { baseDamage: damage * this.dmgMult + this.chip(), critChance: 0, critDamageMult: 1, armor: 0 }, { targetKind: "player", attackerRank: "boss", damageType: "piercing", knockbackFrom: this.group.position, knockbackForce: 6 });
  }

  // ---- antecipação / posição ----
  private trackPlayerVel(dt: number): void {
    const p = this.deps.player.position;
    if (this.velInit && dt > 0) {
      const k = Math.min(1, dt * 8);
      this.playerVel.x += ((p.x - this.lastPlayerPos.x) / dt - this.playerVel.x) * k;
      this.playerVel.z += ((p.z - this.lastPlayerPos.z) / dt - this.playerVel.z) * k;
    }
    this.lastPlayerPos.copy(p);
    this.velInit = true;
  }
  private predictPlayer(t: number, out: THREE.Vector3): THREE.Vector3 {
    const p = this.deps.player.position;
    out.set(p.x + this.playerVel.x * t, 0, p.z + this.playerVel.z * t);
    this.clampToArena(out);
    return out;
  }
  private distToPlayer(): number {
    return Math.hypot(this.deps.player.position.x - this.group.position.x, this.deps.player.position.z - this.group.position.z);
  }
  private updateFacing(dt: number): void {
    if (this.state === "dead") return;
    _v.subVectors(this.deps.player.position, this.group.position); _v.y = 0;
    if (_v.lengthSq() > 0.01) this.facing = dampAngle(this.facing, Math.atan2(_v.x, _v.z), 6, dt);
  }
  private faceInstant(pos: THREE.Vector3): void {
    _v.subVectors(pos, this.group.position); _v.y = 0;
    if (_v.lengthSq() > 0.01) this.facing = Math.atan2(_v.x, _v.z);
  }
  private clampToArena(pos: THREE.Vector3): void {
    const dx = pos.x - this.arenaCx, dz = pos.z - this.arenaCz;
    const d = Math.hypot(dx, dz);
    const lim = this.arenaR + GameConfig.boss.leash.margin - this.radius; // 10.9DA — segue o player pela zona
    if (d > lim && d > 1e-4) { pos.x = this.arenaCx + (dx / d) * lim; pos.z = this.arenaCz + (dz / d) * lim; }
  }

  // ---- morte ----
  private die(): void {
    if (this.state === "dead") return;
    this.state = "dead";
    this.attackName = null;
    this.deathTimer = 1.8;
    this.hookWindow = 0;
    this.pending.length = 0;
    this.deps.telegraph.cancel(this.atkTeleId);
    this.deps.impulse(0.22);
    events.emit(this.isEvent ? "boss:defeatedEvent" : "boss:defeated", undefined);
  }
  private updateDeath(dt: number): void {
    this.deathTimer -= dt;
    const t = Math.max(0, this.deathTimer / 1.8);
    this.group.scale.setScalar(Math.max(0.001, t) * (this.isEvent ? 1.12 : 1));
    this.coreMat.emissiveIntensity = 3 * t;
    this.auraMat.opacity = 0.4 * t;
    this.floatGroup.position.y = cfg.hover + (1 - t) * 2;
    if (Math.random() < dt * 10) this.deps.particles.burst(this.group.position.x + (Math.random() - 0.5) * 3, cfg.hover * t, this.group.position.z + (Math.random() - 0.5) * 3, ACCENT, { count: 6, speed: 4 });
    if (this.deathTimer <= 0) this.removeRequested = true;
  }

  dispose(scene: THREE.Scene): void {
    this.deps.telegraph.cancel(this.atkTeleId);
    scene.remove(this.group);
    this.group.traverse((o) => { if (o instanceof THREE.Mesh) o.geometry?.dispose?.(); });
    this.coreMat.dispose();
    this.auraMat.dispose();
    for (const m of this.ringMats) m.dispose();
  }
}
